import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { NavLink, Outlet, Link, Navigate, useLocation } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import { ACCOUNT_MENU_GROUPS, ACCOUNT_DELETE_PATH } from '../../data/accountMenu';
import {
  readStoredAuth,
  DIGIRASA_AUTH_UPDATED_EVENT,
  buildAuthRequiredRedirectUrl,
} from './profileFormUtils';
import { DIGIRASA_SESSION_EXTENDED_EVENT } from '../../utils/sessionExpiry';
import './AccountLayout.css';

const PUBLIC_ACCOUNT_PATHS = new Set([
  '/account/forgot-password',
  '/account/reset-password',
  '/account/password-updated',
]);

const AccountChromeContext = createContext({
  setFullscreen: () => {},
});

/** Hide the account sidebar while a child screen needs the full width. */
export function useAccountFullscreen(enabled) {
  const { setFullscreen } = useContext(AccountChromeContext);
  useEffect(() => {
    const on = Boolean(enabled);
    setFullscreen(on);
    document.body.classList.toggle('account-fullscreen-open', on);
    return () => {
      setFullscreen(false);
      document.body.classList.remove('account-fullscreen-open');
    };
  }, [enabled, setFullscreen]);
}

const AccountLayout = () => {
  const { pathname, search } = useLocation();
  const [auth, setAuth] = useState(() => readStoredAuth());
  const [outletKey, setOutletKey] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const chrome = useMemo(() => ({ setFullscreen }), []);
  const isPublicAuthSection = PUBLIC_ACCOUNT_PATHS.has(pathname);
  const resetChipActive =
    pathname === '/account/reset-password' || pathname === '/account/forgot-password';
  const chipItems = useMemo(
    () => [
      ...ACCOUNT_MENU_GROUPS.flatMap((group) => group.items),
      { to: ACCOUNT_DELETE_PATH, label: 'Delete account', danger: true },
    ],
    [],
  );

  useEffect(() => {
    const sync = () => setAuth(readStoredAuth());
    const onExtended = () => {
      setAuth(readStoredAuth());
      setOutletKey((k) => k + 1);
    };
    window.addEventListener(DIGIRASA_AUTH_UPDATED_EVENT, sync);
    window.addEventListener(DIGIRASA_SESSION_EXTENDED_EVENT, onExtended);
    return () => {
      window.removeEventListener(DIGIRASA_AUTH_UPDATED_EVENT, sync);
      window.removeEventListener(DIGIRASA_SESSION_EXTENDED_EVENT, onExtended);
    };
  }, []);

  // Forgot/reset stay public for logged-out visitors. Signed-in users coming from
  // the Reset password chip should keep the account nav.
  if (isPublicAuthSection && !auth.token) {
    return (
      <div className="account-layout account-layout--public-auth">
        <div className="account-layout__inner account-layout__inner--single">
          <Link to="/" className="account-layout__back">
            ← Back to site
          </Link>
          <main className="account-layout__main">
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  if (!auth.token) {
    // After delete/logout on this page, do not send users to login with a return
    // path of /account/delete — that sticky redirect wrongly reopens Delete account.
    if (pathname === ACCOUNT_DELETE_PATH) {
      return <Navigate to="/" replace />;
    }
    const returnTo = `${pathname}${search || ''}`;
    return <Navigate to={buildAuthRequiredRedirectUrl(returnTo)} replace />;
  }

  return (
    <AccountChromeContext.Provider value={chrome}>
      <div className={`account-layout${fullscreen ? ' account-layout--fullscreen' : ''}`}>
        <div className={`account-layout__inner${fullscreen ? ' account-layout__inner--full' : ''}`}>
          {fullscreen ? null : (
            <>
              <div className="account-layout__mobile-bar">
                <Link to="/" className="account-layout__back">
                  ← Back to site
                </Link>
                {auth.user?.name ? (
                  <span className="account-layout__mobile-user">{auth.user.name}</span>
                ) : null}
              </div>
              <nav className="account-layout__chips" aria-label="Account sections">
                {chipItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => {
                      const profileEditActive =
                        item.to === '/account' && pathname === '/account/details';
                      const resetMenuActive =
                        item.to === '/account/reset-password' && resetChipActive;
                      const active = isActive || profileEditActive || resetMenuActive;
                      return `account-layout__chip${active ? ' account-layout__chip--active' : ''}${item.danger ? ' account-layout__chip--danger' : ''}`;
                    }}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <aside
                id="account-sections-nav"
                className="account-layout__sidebar"
                aria-label="Account sections"
              >
              <Link to="/" className="account-layout__back">
                ← Back to site
              </Link>
              <div className="account-layout__user">
                <strong>{auth.user?.name || 'My account'}</strong>
                {auth.user?.email ? (
                  <span className="account-layout__email">{auth.user.email}</span>
                ) : null}
              </div>
              <nav className="account-layout__nav">
                {ACCOUNT_MENU_GROUPS.map((group) => (
                  <div key={group.label} className="account-layout__nav-group">
                    <div className="account-layout__nav-label">{group.label}</div>
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) => {
                          const profileEditActive =
                            item.to === '/account' && pathname === '/account/details';
                          const resetMenuActive =
                            item.to === '/account/reset-password' && resetChipActive;
                          const active = isActive || profileEditActive || resetMenuActive;
                          return `account-layout__nav-link${active ? ' account-layout__nav-link--active' : ''}`;
                        }}
                      >
                        {item.label}
                        <FaChevronRight className="account-layout__nav-chevron" aria-hidden />
                      </NavLink>
                    ))}
                  </div>
                ))}
                <div className="account-layout__separator" />
                <NavLink
                  to={ACCOUNT_DELETE_PATH}
                  className={({ isActive }) =>
                    `account-layout__nav-link account-layout__nav-link--danger${isActive ? ' account-layout__nav-link--active' : ''}`
                  }
                >
                  Delete account
                  <FaChevronRight className="account-layout__nav-chevron" aria-hidden />
                </NavLink>
              </nav>
            </aside>
            </>
          )}
          <main className="account-layout__main">
            <Outlet key={outletKey} />
          </main>
        </div>
      </div>
    </AccountChromeContext.Provider>
  );
};

export default AccountLayout;
